import React, { useCallback, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Descriptions,
    Empty,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import nodeAPI from "../../../service/ApiServiceNode";
import "./SentryPrismaScanPage.scss";
import { getApiErrorMessage } from '../../../utils/errorUtils';
import logger from "../../../utils/logger";



const { Title, Paragraph, Text } = Typography;

const SENTRY_BASE_URL = "https://mediatropy-sentry.sentry.io/issues";

const ISSUE_TYPE_OPTIONS = [
    {
        value: "project_invoice_allocations",
        label: "Broken Invoice Links on Projects",
        description:
            "A project's sales order still points to an invoice that was deleted (e.g. voided in Xero then removed). Every time anyone opens the Projects page, Revenue Recognition, Vendor Planning, or Client List, it crashes. Even a single broken link causes hundreds of errors because every page load hits it.",
        sentryIssues: [
            { id: "7260188114", shortId: "HRP-BACKEND-Q", endpoint: "Projects page", count: 219, status: "unresolved" },
            { id: "7260380428", shortId: "HRP-BACKEND-W", endpoint: "Vendor Rev Recognition breakdown", count: 13 },
            { id: "7260380934", shortId: "HRP-BACKEND-X", endpoint: "Agency Rev Recognition breakdown", count: 10 },
            { id: "7260431155", shortId: "HRP-BACKEND-12", endpoint: "Agency Rev Recognition breakdown", count: 8 },
            { id: "7260427412", shortId: "HRP-BACKEND-11", endpoint: "Revenue Forecast export", count: 7 },
            { id: "7260212382", shortId: "HRP-BACKEND-R", endpoint: "Vendor invoices by project", count: 7 },
            { id: "7260177225", shortId: "HRP-BACKEND-P", endpoint: "Client list", count: 6 },
            { id: "7254672139", shortId: "HRP-BACKEND-1", endpoint: "Vendor planning by project", count: 6 },
            { id: "7260377831", shortId: "HRP-BACKEND-V", endpoint: "Vendor Rev Recognition report", count: 5 },
            { id: "7260381222", shortId: "HRP-BACKEND-Z", endpoint: "Agency Rev Recognition report", count: 2 },
            { id: "7260281890", shortId: "HRP-BACKEND-S", endpoint: "Project issues report", count: 1 },
        ],
    },
    {
        value: "so_invoice_allocations",
        label: "Broken Invoice Links on Sales Orders",
        description:
            "A sales order still points to an invoice that was deleted. Crashes the Sales Orders listing page in Finance. This includes links that may not be tied to any specific project.",
        sentryIssues: [
            { id: "7260377078", shortId: "HRP-BACKEND-T", endpoint: "Sales Orders page", count: 16 },
        ],
    },
    {
        value: "revenue_allocation_orphans",
        label: "Broken Revenue Allocation Records",
        description:
            "Monthly revenue allocation records that reference a deleted invoice. Crashes the Agency Revenue Recognition report. These records are auto-generated by the system — safe to delete because they get recreated automatically the next time the report runs.",
        sentryIssues: [
            { id: "7260380991", shortId: "HRP-BACKEND-Y", endpoint: "Agency Rev Recognition report", count: 2, status: "unresolved" },
            { id: "7261000000", shortId: "HRP-BACKEND-16", endpoint: "Agency Rev Recognition breakdown", count: 1, status: "unresolved" },
        ],
    },
];
const ISSUE_TYPE_LABEL_MAP = Object.fromEntries(
    ISSUE_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

const DEFAULT_SCAN_LIMIT = 500;

const extractApiError = (error, fallbackMessage) => {
    const status = error?.response?.status;
    const payload = error?.response?.data;
    const messageText =
        payload?.data?.message ||
        payload?.message ||
        payload?.data?.error ||
        payload?.error ||
        error?.message ||
        fallbackMessage;

    return {
        status,
        message: messageText || fallbackMessage,
    };
};

const formatDate = (value) => {
    if (!value) {
        return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "-";
    }
    return date.toISOString().replace("T", " ").replace("Z", " UTC");
};

const toNumber = (value, fallback = null) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeActionPayload = (actions) =>
    actions.map((action) => {
        if (action.source_table === "agency_revenue_allocation_monthly") {
            return {
                type: "remove",
                source_table: "agency_revenue_allocation_monthly",
                revenue_allocation_id: action.revenue_allocation_id,
            };
        }
        if (action.type === "relink") {
            return {
                type: "relink",
                source_table: "sales_order_invoice",
                sales_order_invoice_id: action.sales_order_invoice_id,
                new_invoice_id: action.new_invoice_id,
            };
        }
        return {
            type: "remove",
            source_table: "sales_order_invoice",
            sales_order_invoice_id: action.sales_order_invoice_id,
        };
    });

const mergeInvoiceOptions = (candidateInvoices = [], searchedInvoices = []) => {
    const map = new Map();

    candidateInvoices.forEach((invoice) => {
        const id = toNumber(invoice.invoice_id ?? invoice.id, null);
        if (!id) {
            return;
        }

        map.set(id, {
            id,
            invoice_no: invoice.invoice_no || `Invoice #${id}`,
            xero_status: invoice.xero_status || null,
            invoice_date: invoice.invoice_date || null,
            source: "candidate",
        });
    });

    searchedInvoices.forEach((invoice) => {
        const id = toNumber(invoice.id ?? invoice.invoice_id, null);
        if (!id) {
            return;
        }

        map.set(id, {
            id,
            invoice_no: invoice.invoice_no || `Invoice #${id}`,
            xero_status: invoice.xero_status || null,
            invoice_date: invoice.invoice_date || null,
            linked_to_sales_order: !!invoice.linked_to_sales_order,
            source: "search",
        });
    });

    return Array.from(map.values()).sort((a, b) => {
        const aLinked = a.linked_to_sales_order ? 1 : 0;
        const bLinked = b.linked_to_sales_order ? 1 : 0;
        if (aLinked !== bLinked) {
            return bLinked - aLinked;
        }

        const aDate = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        const bDate = b.invoice_date ? new Date(b.invoice_date).getTime() : 0;
        if (aDate !== bDate) {
            return bDate - aDate;
        }
        return b.id - a.id;
    });
};

function SentryPrismaScanPage({ showJson = false }) {
    const [selectedIssueTypes, setSelectedIssueTypes] = useState(
        ISSUE_TYPE_OPTIONS.map((item) => item.value)
    );
    const [scanLoading, setScanLoading] = useState(false);
    const [scanSummary, setScanSummary] = useState(null);
    const [scanIssueSummaries, setScanIssueSummaries] = useState([]);
    const [scanRows, setScanRows] = useState([]);
    const [scanRaw, setScanRaw] = useState(null);
    const [scanFeedback, setScanFeedback] = useState(null);

    const [draftActions, setDraftActions] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewRows, setPreviewRows] = useState([]);
    const [previewSummary, setPreviewSummary] = useState(null);
    const [confirmToken, setConfirmToken] = useState("");

    const [applyLoading, setApplyLoading] = useState(false);
    const [applyResult, setApplyResult] = useState(null);

    const [editingRow, setEditingRow] = useState(null);
    const [relinkInvoiceId, setRelinkInvoiceId] = useState(null);
    const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
    const [invoiceSearchLoading, setInvoiceSearchLoading] = useState(false);
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [relinkModalVisible, setRelinkModalVisible] = useState(false);

    const getRowKey = useCallback((row) => {
        if (row.source_table === "agency_revenue_allocation_monthly") {
            return `ram:${row.revenue_allocation_id}`;
        }
        return `soi:${row.sales_order_invoice_id}`;
    }, []);

    const draftActionMap = useMemo(() => {
        const map = new Map();
        draftActions.forEach((action) => {
            const key = action.source_table === "agency_revenue_allocation_monthly"
                ? `ram:${action.revenue_allocation_id}`
                : `soi:${action.sales_order_invoice_id}`;
            map.set(key, action);
        });
        return map;
    }, [draftActions]);

    const upsertDraftAction = useCallback((action) => {
        setDraftActions((current) => {
            const key = action.source_table === "agency_revenue_allocation_monthly"
                ? `ram:${action.revenue_allocation_id}`
                : `soi:${action.sales_order_invoice_id}`;
            const map = new Map(current.map((item) => {
                const k = item.source_table === "agency_revenue_allocation_monthly"
                    ? `ram:${item.revenue_allocation_id}`
                    : `soi:${item.sales_order_invoice_id}`;
                return [k, item];
            }));
            map.set(key, action);
            return Array.from(map.values());
        });
    }, []);

    const runScan = useCallback(async () => {
        if (selectedIssueTypes.length === 0) {
            const feedback = {
                type: "warning",
                text: "Select at least one issue type to scan.",
            };
            setScanFeedback(feedback);
            message.warning(feedback.text);
            return;
        }

        setScanLoading(true);
        setApplyResult(null);
        setScanFeedback(null);

        try {
            const payload = {
                issueTypes: selectedIssueTypes,
                limit: DEFAULT_SCAN_LIMIT,
            };

            const response = await nodeAPI.post("/report/sentry-prisma-scan/scan", payload);
            const data = response.data?.data || response.data;

            const rows = data.rows || [];
            setScanRows(rows);
            setScanSummary(data.summary || null);
            setScanIssueSummaries(data.issue_summaries || []);
            setScanRaw(data);

            const rowKeySet = new Set(rows.map((row) => {
                if (row.source_table === "agency_revenue_allocation_monthly") return `ram:${row.revenue_allocation_id}`;
                return `soi:${row.sales_order_invoice_id}`;
            }));
            setDraftActions((current) => current.filter((action) => {
                const key = action.source_table === "agency_revenue_allocation_monthly"
                    ? `ram:${action.revenue_allocation_id}`
                    : `soi:${action.sales_order_invoice_id}`;
                return rowKeySet.has(key);
            }));

            if (rows.length > 0) {
                const feedback = {
                    type: "success",
                    text: `Scan complete: found ${rows.length} broken rows.`,
                };
                setScanFeedback(feedback);
                message.success(feedback.text);
            } else {
                const feedback = {
                    type: "info",
                    text: "Scan complete: no matching broken rows found for selected issue types.",
                };
                setScanFeedback(feedback);
                message.info(feedback.text);
            }
        } catch (error) {
            logger.error("Failed to scan sentry prisma issues:", error);
            const apiError = extractApiError(error, "Failed to scan issue rows");
            const feedback = {
                type: "error",
                text: apiError.status
                    ? `Scan failed (${apiError.status}): ${apiError.message}`
                    : `Scan failed: ${apiError.message}`,
            };
            setScanFeedback(feedback);
            message.error(feedback.text);
        } finally {
            setScanLoading(false);
        }
    }, [selectedIssueTypes]);

    const removeDraftAction = (row) => {
        setDraftActions((current) => {
            const targetKey = row.source_table === "agency_revenue_allocation_monthly"
                ? `ram:${row.revenue_allocation_id}`
                : `soi:${row.sales_order_invoice_id}`;
            return current.filter((action) => {
                const key = action.source_table === "agency_revenue_allocation_monthly"
                    ? `ram:${action.revenue_allocation_id}`
                    : `soi:${action.sales_order_invoice_id}`;
                return key !== targetKey;
            });
        });
    };

    const queueRemoveAction = (row) => {
        if (row.source_table === "agency_revenue_allocation_monthly") {
            upsertDraftAction({
                type: "remove",
                source_table: "agency_revenue_allocation_monthly",
                revenue_allocation_id: row.revenue_allocation_id,
                sales_order_no: row.sales_order_no,
                project_code: row.project_code,
                current_invoice_id: row.invoice_id,
            });
            message.success(`Remove action queued for revenue allocation #${row.revenue_allocation_id}`);
        } else {
            upsertDraftAction({
                type: "remove",
                source_table: "sales_order_invoice",
                sales_order_invoice_id: row.sales_order_invoice_id,
                sales_order_no: row.sales_order_no,
                project_code: row.project_code,
                current_invoice_id: row.invoice_id,
            });
            message.success(`Remove action queued for row #${row.sales_order_invoice_id}`);
        }
    };

    const openRelinkModal = (row) => {
        setEditingRow(row);
        setRelinkInvoiceId(row.invoice_id || null);
        setInvoiceSearchTerm("");
        setInvoiceOptions(mergeInvoiceOptions(row.candidate_invoices || [], []));
        setRelinkModalVisible(true);
    };

    const closeRelinkModal = () => {
        setRelinkModalVisible(false);
        setEditingRow(null);
        setRelinkInvoiceId(null);
        setInvoiceSearchTerm("");
        setInvoiceOptions([]);
    };

    const searchInvoices = async () => {
        if (!editingRow) {
            return;
        }

        setInvoiceSearchLoading(true);
        try {
            const response = await nodeAPI.get("/report/sentry-prisma-scan/invoices/search", {
                params: {
                    q: invoiceSearchTerm.trim(),
                    salesOrderId: editingRow.sales_order_id || undefined,
                    limit: 25,
                },
            });

            const data = response.data?.data || response.data;
            const merged = mergeInvoiceOptions(editingRow.candidate_invoices || [], data.invoices || []);
            setInvoiceOptions(merged);
        } catch (error) {
            logger.error("Failed to search invoices:", error);
            message.error(getApiErrorMessage(error, "Failed to search invoices"));
        } finally {
            setInvoiceSearchLoading(false);
        }
    };

    const queueRelinkAction = () => {
        if (!editingRow) {
            return;
        }

        const invoiceId = toNumber(relinkInvoiceId, null);
        if (!invoiceId || invoiceId <= 0) {
            message.warning("Please provide a valid target invoice ID");
            return;
        }

        upsertDraftAction({
            type: "relink",
            sales_order_invoice_id: editingRow.sales_order_invoice_id,
            new_invoice_id: invoiceId,
            sales_order_no: editingRow.sales_order_no,
            project_code: editingRow.project_code,
            current_invoice_id: editingRow.invoice_id,
        });

        message.success(`Relink action queued for row #${editingRow.sales_order_invoice_id}`);
        closeRelinkModal();
    };

    const previewFixes = async () => {
        if (draftActions.length === 0) {
            message.warning("No draft actions to preview");
            return;
        }

        setPreviewLoading(true);
        try {
            const response = await nodeAPI.post("/report/sentry-prisma-scan/preview-fixes", {
                actions: normalizeActionPayload(draftActions),
            });
            const data = response.data?.data || response.data;

            setPreviewRows(data.preview || []);
            setPreviewSummary(data.summary || null);
            setConfirmToken(data.confirm_token || "");
            setPreviewVisible(true);

            if ((data.summary?.invalid_count || 0) > 0) {
                message.warning("Preview contains invalid actions. Review before applying.");
            } else {
                message.success("Preview generated");
            }
        } catch (error) {
            logger.error("Failed to preview fixes:", error);
            message.error(getApiErrorMessage(error, "Failed to preview fixes"));
        } finally {
            setPreviewLoading(false);
        }
    };

    const applyFixes = async () => {
        if (!confirmToken) {
            message.error("Missing confirmation token. Please preview again.");
            return;
        }

        setApplyLoading(true);
        try {
            const response = await nodeAPI.post("/report/sentry-prisma-scan/apply-fixes", {
                actions: normalizeActionPayload(draftActions),
                confirmToken,
            });

            const data = response.data?.data || response.data;
            setApplyResult(data);
            setPreviewVisible(false);

            const failedKeys = new Set(
                (data.results || [])
                    .filter((item) => item.status === "error")
                    .map((item) => {
                        if (item.source_table === "agency_revenue_allocation_monthly") return `ram:${item.revenue_allocation_id}`;
                        return `soi:${item.sales_order_invoice_id}`;
                    })
            );
            setDraftActions((current) =>
                current.filter((action) => {
                    const key = action.source_table === "agency_revenue_allocation_monthly"
                        ? `ram:${action.revenue_allocation_id}`
                        : `soi:${action.sales_order_invoice_id}`;
                    return failedKeys.has(key);
                })
            );

            const summary = data.summary || {};
            if ((summary.failure_count || 0) > 0) {
                message.warning(
                    `Applied with errors: ${summary.success_count || 0} success, ${summary.failure_count || 0} failed`
                );
            } else {
                message.success(`Applied ${summary.success_count || 0} actions successfully`);
            }

            await runScan();
        } catch (error) {
            logger.error("Failed to apply fixes:", error);
            message.error(getApiErrorMessage(error, "Failed to apply fixes"));
        } finally {
            setApplyLoading(false);
        }
    };

    const scanColumns = [
        {
            title: "Table / ID",
            key: "row_id",
            width: 140,
            render: (_, row) => {
                const isRam = row.source_table === "agency_revenue_allocation_monthly";
                return (
                    <div>
                        <Tag color={isRam ? "orange" : "blue"} style={{ fontSize: 10 }}>
                            {isRam ? "RAM" : "SOI"}
                        </Tag>
                        <div>#{isRam ? row.revenue_allocation_id : row.sales_order_invoice_id}</div>
                    </div>
                );
            },
        },
        {
            title: "Sales Order",
            key: "sales_order",
            render: (_, row) => (
                <div>
                    <div>{row.sales_order_no || "-"}</div>
                    <Text type="secondary">ID: {row.sales_order_id || "-"}</Text>
                </div>
            ),
        },
        {
            title: "Project",
            key: "project",
            render: (_, row) => (
                <div>
                    <div>{row.project_code || "-"}</div>
                    <Text type="secondary">{row.project_name || "-"}</Text>
                </div>
            ),
        },
        {
            title: "Broken Invoice",
            key: "invoice",
            render: (_, row) => (
                <div>
                    <Text code>{row.invoice_id ?? "NULL"}</Text>
                    <div>
                        <Tag color={row.broken_reason === "INVOICE_ID_NULL" ? "gold" : "red"}>
                            {row.broken_reason}
                        </Tag>
                    </div>
                    {row.source_table === "agency_revenue_allocation_monthly" && row.month && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Month: {row.month.substring(0, 7)}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: "Detected Issue Types",
            key: "detected_issue_types",
            render: (_, row) => {
                const types = Array.isArray(row.detected_issue_types) ? row.detected_issue_types : [];
                if (types.length === 0) {
                    return <Text type="secondary">-</Text>;
                }

                const rowKey = row.source_table === "agency_revenue_allocation_monthly"
                    ? row.revenue_allocation_id
                    : row.sales_order_invoice_id;

                return (
                    <Space wrap>
                        {types.map((type) => (
                            <Tag key={`${rowKey}-${type}`} color="purple">
                                {ISSUE_TYPE_LABEL_MAP[type] || type}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: "Candidates",
            key: "candidates",
            render: (_, row) => {
                if (row.source_table === "agency_revenue_allocation_monthly") {
                    return (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            System-generated — safe to remove
                        </Text>
                    );
                }

                const candidates = row.candidate_invoices || [];
                if (candidates.length === 0) {
                    return <Text type="secondary">No candidates</Text>;
                }

                const rowKey = row.sales_order_invoice_id;
                return (
                    <Space direction="vertical" size={2}>
                        {candidates.slice(0, 3).map((candidate) => (
                            <Text key={`${rowKey}-${candidate.invoice_id}`}>
                                {candidate.invoice_no || `Invoice #${candidate.invoice_id}`} ({candidate.invoice_id})
                            </Text>
                        ))}
                        {candidates.length > 3 && (
                            <Text type="secondary">+{candidates.length - 3} more</Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            width: 220,
            render: (_, row) => {
                const isRam = row.source_table === "agency_revenue_allocation_monthly";
                const key = isRam ? `ram:${row.revenue_allocation_id}` : `soi:${row.sales_order_invoice_id}`;
                const draftAction = draftActionMap.get(key);

                return (
                    <Space direction="vertical" size={6}>
                        <Space>
                            {!isRam && (
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => openRelinkModal(row)}
                                >
                                    Relink
                                </Button>
                            )}
                            <Button
                                danger
                                size="small"
                                onClick={() => queueRemoveAction(row)}
                            >
                                Queue Remove
                            </Button>
                        </Space>
                        {draftAction && (
                            <Tag color={draftAction.type === "remove" ? "volcano" : "blue"}>
                                Queued: {draftAction.type.toUpperCase()}
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
    ];

    const draftColumns = [
        {
            title: "Table / ID",
            key: "row_id",
            width: 140,
            render: (_, row) => {
                const isRam = row.source_table === "agency_revenue_allocation_monthly";
                return (
                    <div>
                        <Tag color={isRam ? "orange" : "blue"} style={{ fontSize: 10 }}>
                            {isRam ? "RAM" : "SOI"}
                        </Tag>
                        <div>#{isRam ? row.revenue_allocation_id : row.sales_order_invoice_id}</div>
                    </div>
                );
            },
        },
        {
            title: "Action",
            dataIndex: "type",
            key: "type",
            width: 120,
            render: (type) => (
                <Tag color={type === "remove" ? "volcano" : "blue"}>{String(type).toUpperCase()}</Tag>
            ),
        },
        {
            title: "Current",
            dataIndex: "current_invoice_id",
            key: "current_invoice_id",
            render: (value) => <Text code>{value ?? "NULL"}</Text>,
        },
        {
            title: "Proposed",
            dataIndex: "new_invoice_id",
            key: "new_invoice_id",
            render: (value, row) => (row.type === "relink" ? <Text code>{value}</Text> : <Text type="secondary">Delete row</Text>),
        },
        {
            title: "Context",
            key: "context",
            render: (_, row) => (
                <Text type="secondary">
                    {row.sales_order_no || "-"} / {row.project_code || "-"}
                </Text>
            ),
        },
        {
            title: "Control",
            key: "control",
            width: 90,
            render: (_, row) => (
                <Button
                    size="small"
                    onClick={() => removeDraftAction(row)}
                >
                    Remove
                </Button>
            ),
        },
    ];

    const previewColumns = [
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (status) => (
                <Tag color={status === "valid" ? "green" : "red"}>{String(status).toUpperCase()}</Tag>
            ),
        },
        {
            title: "Action",
            dataIndex: "type",
            key: "type",
            width: 110,
            render: (type) => (
                <Tag color={type === "remove" ? "volcano" : "blue"}>{String(type).toUpperCase()}</Tag>
            ),
        },
        {
            title: "Table / ID",
            key: "row_id",
            width: 140,
            render: (_, row) => {
                const isRam = row.source_table === "agency_revenue_allocation_monthly";
                return (
                    <div>
                        <Tag color={isRam ? "orange" : "blue"} style={{ fontSize: 10 }}>
                            {isRam ? "RAM" : "SOI"}
                        </Tag>
                        <div>#{isRam ? row.revenue_allocation_id : row.sales_order_invoice_id}</div>
                    </div>
                );
            },
        },
        {
            title: "Current Invoice",
            key: "current_invoice_id",
            render: (_, row) => <Text code>{row.current_invoice_id ?? "NULL"}</Text>,
        },
        {
            title: "Proposed Invoice",
            key: "proposed_invoice_id",
            render: (_, row) =>
                row.type === "relink" ? (
                    <Text code>{row.proposed_invoice_id ?? row.new_invoice_id ?? "-"}</Text>
                ) : (
                    <Text type="secondary">Delete row</Text>
                ),
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message",
        },
    ];

    const applyColumns = [
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => {
                const color = status === "success" ? "green" : status === "skipped" ? "gold" : "red";
                return <Tag color={color}>{String(status).toUpperCase()}</Tag>;
            },
        },
        {
            title: "Action",
            dataIndex: "type",
            key: "type",
            width: 120,
            render: (type) => (
                <Tag color={type === "remove" ? "volcano" : "blue"}>{String(type).toUpperCase()}</Tag>
            ),
        },
        {
            title: "Table / ID",
            key: "row_id",
            width: 140,
            render: (_, row) => {
                const isRam = row.source_table === "agency_revenue_allocation_monthly";
                return (
                    <div>
                        <Tag color={isRam ? "orange" : "blue"} style={{ fontSize: 10 }}>
                            {isRam ? "RAM" : "SOI"}
                        </Tag>
                        <div>#{isRam ? row.revenue_allocation_id : row.sales_order_invoice_id}</div>
                    </div>
                );
            },
        },
        {
            title: "Old Invoice",
            dataIndex: "old_invoice_id",
            key: "old_invoice_id",
            render: (value) => <Text code>{value ?? "NULL"}</Text>,
        },
        {
            title: "New Invoice",
            dataIndex: "new_invoice_id",
            key: "new_invoice_id",
            render: (value) => <Text code>{value ?? "NULL"}</Text>,
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message",
        },
    ];

    if (showJson) {
        return (
            <div className="sentry-prisma-page">
                <Card>
                    <Space className="json-toolbar">
                        <Button type="primary" onClick={runScan} loading={scanLoading}>
                            Run Scan
                        </Button>
                    </Space>
                    <pre className="sentry-prisma-json">
                        {JSON.stringify(scanRaw || { summary: scanSummary, rows: scanRows }, null, 2)}
                    </pre>
                </Card>
            </div>
        );
    }

    return (
        <div className="sentry-prisma-page">
            <Card className="sentry-prisma-header">
                <Title level={3}>Broken Invoice Link Scanner</Title>
                <Paragraph>
                    Finds and fixes records that still point to invoices that were deleted (e.g. voided in Xero then
                    removed from the system). A single broken link can cause hundreds of errors because every page load
                    that touches the affected project or sales order will crash.
                </Paragraph>
                <Alert
                    type="info"
                    showIcon
                    message="How it works"
                    description={
                        <div>
                            <div>
                                The scanner checks three types of broken links. For each one found, you can either{" "}
                                <Text strong>Relink</Text> it to a valid invoice or <Text strong>Remove</Text> the broken record.
                                Revenue allocation records can only be removed (they auto-regenerate on next report run).
                            </div>
                            <div style={{ marginTop: 4 }}>
                                <Text type="secondary">
                                    Linked Sentry issues:{" "}
                                    {ISSUE_TYPE_OPTIONS.reduce((sum, opt) => sum + (opt.sentryIssues?.length || 0), 0)}{" "}
                                    ({ISSUE_TYPE_OPTIONS.reduce((sum, opt) => sum + (opt.sentryIssues || []).reduce((s, i) => s + i.count, 0), 0)}{" "}
                                    total occurrences)
                                </Text>
                            </div>
                        </div>
                    }
                />
            </Card>

            <Card className="sentry-prisma-filters" title="What to scan">
                <Paragraph>
                    Select which types of broken links to look for, then click <Text strong>Run Scan</Text>.
                    Each type shows the Sentry error IDs and affected pages.
                </Paragraph>

                {scanFeedback && (
                    <Alert
                        className="scan-feedback-alert"
                        type={scanFeedback.type}
                        showIcon
                        message={scanFeedback.text}
                    />
                )}

                <Space direction="vertical" className="issue-type-list" style={{ width: "100%" }}>
                    {ISSUE_TYPE_OPTIONS.map((option) => (
                        <div key={option.value} className="issue-type-item">
                            <Checkbox
                                checked={selectedIssueTypes.includes(option.value)}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    setSelectedIssueTypes((current) => {
                                        if (checked) {
                                            return Array.from(new Set([...current, option.value]));
                                        }
                                        return current.filter((value) => value !== option.value);
                                    });
                                }}
                            >
                                <Text strong>{option.label}</Text>
                            </Checkbox>
                            <div className="issue-type-meta">
                                <Text type="secondary">{option.description}</Text>
                                <div style={{ marginTop: 6 }}>
                                    {(option.sentryIssues || []).map((issue) => (
                                        <Tag key={issue.id} color={issue.status === "unresolved" ? "error" : "processing"}>
                                            <a
                                                href={`${SENTRY_BASE_URL}/${issue.id}/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "inherit", textDecoration: "none" }}
                                            >
                                                {issue.shortId}
                                            </a>
                                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                                                {issue.endpoint} ({issue.count})
                                            </Text>
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </Space>

                <Row gutter={12} className="filters-row-bottom">
                    <Col xs={24} className="scan-controls">
                        <Space>
                            <Button
                                type="primary"
                                onClick={runScan}
                                loading={scanLoading}
                                disabled={selectedIssueTypes.length === 0}
                            >
                                Run Scan
                            </Button>
                            <Button
                                onClick={() => {
                                    setSelectedIssueTypes(ISSUE_TYPE_OPTIONS.map((item) => item.value));
                                }}
                            >
                                Reset
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {scanSummary && (
                <Card className="sentry-prisma-summary">
                    <Row gutter={12}>
                        <Col xs={24} md={6}>
                            <Statistic title="Total Broken Rows" value={scanSummary.total_broken || 0} />
                        </Col>
                        <Col xs={24} md={6}>
                            <Statistic title="Missing Invoice" value={scanSummary.missing_invoice_count || 0} />
                        </Col>
                        <Col xs={24} md={6}>
                            <Statistic title="NULL Invoice ID" value={scanSummary.null_invoice_id_count || 0} />
                        </Col>
                        <Col xs={24} md={6}>
                            <Statistic title="Returned Rows" value={scanSummary.returned_count || 0} />
                        </Col>
                    </Row>
                    <Descriptions size="small" column={2} className="summary-meta">
                        <Descriptions.Item label="Generated (UTC)">
                            {formatDate(scanSummary.generated_at_utc)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Affected Projects">
                            {scanSummary.affected_projects || 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Affected Sales Orders">
                            {scanSummary.affected_sales_orders || 0}
                        </Descriptions.Item>
                        <Descriptions.Item label="Scan Limit">
                            {scanSummary.limit || 0}
                        </Descriptions.Item>
                    </Descriptions>

                    {scanIssueSummaries.length > 0 && (
                        <div className="issue-summary-list">
                            {scanIssueSummaries.map((item) => (
                                <Tag key={item.issue_type} color="geekblue">
                                    {item.label}: {item.matched_rows}
                                </Tag>
                            ))}
                        </div>
                    )}
                </Card>
            )}

            <Card
                className="sentry-prisma-results"
                title={`Broken Rows (${scanRows.length})`}
                extra={
                    <Space>
                        <Button onClick={runScan} loading={scanLoading}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    rowKey={getRowKey}
                    loading={scanLoading}
                    dataSource={scanRows}
                    columns={scanColumns}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    locale={{
                        emptyText: <Empty description="No broken rows found for current filters." />,
                    }}
                />
            </Card>

            <Card
                className="sentry-prisma-drafts"
                title={`Draft Fixes (${draftActions.length})`}
                extra={
                    <Button
                        type="primary"
                        onClick={previewFixes}
                        disabled={draftActions.length === 0}
                        loading={previewLoading}
                    >
                        Preview Fixes
                    </Button>
                }
            >
                <Table
                    rowKey={(row) => {
                        if (row.source_table === "agency_revenue_allocation_monthly") return `ram:${row.revenue_allocation_id}`;
                        return `soi:${row.sales_order_invoice_id}`;
                    }}
                    dataSource={draftActions}
                    columns={draftColumns}
                    pagination={false}
                    locale={{
                        emptyText: "No queued actions. Use Relink or Queue Remove on a row above.",
                    }}
                />
            </Card>

            {applyResult && (
                <Card className="sentry-prisma-apply" title="Last Apply Result">
                    <Row gutter={12} className="apply-summary">
                        <Col xs={24} md={8}>
                            <Statistic title="Success" value={applyResult.summary?.success_count || 0} />
                        </Col>
                        <Col xs={24} md={8}>
                            <Statistic title="Skipped" value={applyResult.summary?.skipped_count || 0} />
                        </Col>
                        <Col xs={24} md={8}>
                            <Statistic title="Failed" value={applyResult.summary?.failure_count || 0} />
                        </Col>
                    </Row>
                    <Table
                        rowKey={(row, index) => {
                            const id = row.source_table === "agency_revenue_allocation_monthly"
                                ? row.revenue_allocation_id
                                : row.sales_order_invoice_id;
                            return `${row.source_table || "soi"}-${id}-${row.type}-${index}`;
                        }}
                        dataSource={applyResult.results || []}
                        columns={applyColumns}
                        pagination={{ pageSize: 10 }}
                    />
                </Card>
            )}

            <Modal
                title={editingRow ? `Relink Row #${editingRow.sales_order_invoice_id}` : "Relink Row"}
                open={relinkModalVisible}
                onCancel={closeRelinkModal}
                onOk={queueRelinkAction}
                okText="Queue Relink"
                destroyOnClose
            >
                {editingRow && (
                    <Space direction="vertical" size={12} className="modal-stack">
                        <Alert
                            type="warning"
                            showIcon
                            message="Set a valid invoice_id and queue the change. It will only be written after Preview + Confirm Apply."
                        />
                        <Descriptions size="small" bordered column={1}>
                            <Descriptions.Item label="Sales Order">{editingRow.sales_order_no || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Project">{editingRow.project_code || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Current Broken invoice_id">
                                <Text code>{editingRow.invoice_id ?? "NULL"}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <div>
                            <Text type="secondary">Target invoice_id</Text>
                            <InputNumber
                                value={relinkInvoiceId}
                                onChange={(value) => setRelinkInvoiceId(toNumber(value, null))}
                                style={{ width: "100%" }}
                                min={1}
                            />
                        </div>

                        <div>
                            <Text type="secondary">Pick from candidate/search results</Text>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select invoice"
                                value={relinkInvoiceId || undefined}
                                onChange={(value) => setRelinkInvoiceId(toNumber(value, null))}
                                options={invoiceOptions.map((option) => ({
                                    value: option.id,
                                    label: `${option.invoice_no} (${option.id})${option.xero_status ? ` - ${option.xero_status}` : ""}`,
                                }))}
                                showSearch
                                optionFilterProp="label"
                            />
                        </div>

                        <Space.Compact style={{ width: "100%" }}>
                            <Input
                                value={invoiceSearchTerm}
                                onChange={(event) => setInvoiceSearchTerm(event.target.value)}
                                placeholder="Search by invoice no or ID"
                                onPressEnter={searchInvoices}
                            />
                            <Button loading={invoiceSearchLoading} onClick={searchInvoices}>
                                Search
                            </Button>
                        </Space.Compact>
                    </Space>
                )}
            </Modal>

            <Modal
                title="Preview Fixes"
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                onOk={applyFixes}
                okText="Confirm Apply"
                okButtonProps={{
                    loading: applyLoading,
                    disabled: (previewSummary?.valid_count || 0) === 0,
                }}
                width={1200}
            >
                <Row gutter={12} className="preview-summary">
                    <Col xs={24} md={8}>
                        <Statistic title="Total" value={previewSummary?.total || 0} />
                    </Col>
                    <Col xs={24} md={8}>
                        <Statistic title="Valid" value={previewSummary?.valid_count || 0} />
                    </Col>
                    <Col xs={24} md={8}>
                        <Statistic title="Invalid" value={previewSummary?.invalid_count || 0} />
                    </Col>
                </Row>
                <Table
                    rowKey={(row, index) => {
                        const id = row.source_table === "agency_revenue_allocation_monthly"
                            ? row.revenue_allocation_id
                            : row.sales_order_invoice_id;
                        return `${row.source_table || "soi"}-${id || "missing"}-${row.type}-${index}`;
                    }}
                    dataSource={previewRows}
                    columns={previewColumns}
                    pagination={{ pageSize: 10 }}
                />
            </Modal>
        </div>
    );
}

export default SentryPrismaScanPage;
